/**
 * Estadísticas del WhatsApp oficial (`/activa/oficial/estadisticas`): lo que pasó con los envíos,
 * cómo se contestó a quienes escribieron y lo que Twilio informa de la cuenta (§B.7.1).
 *
 * En dos niveles: arriba lo que mira el negocio (envíos, lectura, respuestas, bajas, gasto,
 * conversaciones, plantillas) y abajo, plegado, el «Detalle técnico» (tiempos por tramo, códigos de
 * error, lo que factura Twilio, la cuenta y el número). Antes eran 14 bloques al mismo nivel y los
 * resultados quedaban mezclados con los códigos de error de Twilio.
 *
 * - El rango vive en la URL (`?rango=7|30|90|mes|mes_pasado` o `?desde=&hasta=`): un enlace
 *   copiado abre lo mismo, y F5 no lo pierde.
 * - Cuatro pedidos en paralelo (`marca`, `plantillas`, `conversaciones`, `twilio`), cada uno con su
 *   esqueleto y su error. Un 502 de Twilio pone «No pudimos traer…» con «Reintentar» sólo en las
 *   tarjetas de Twilio: nunca voltea la página. Por eso no hay un `Promise.all`.
 * - Un cambio de rango descarta las respuestas del rango anterior que lleguen tarde (un rango
 *   viejo pintado sobre el nuevo es un número mentiroso).
 * - Nada de esto mueve plata: lo que informa Twilio se muestra, y lo que se cobra sigue saliendo
 *   de la tabla de tarifas.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCheck, Download, Eye, Loader2, MessageSquare, RefreshCw, Send, UserMinus, Wallet } from "lucide-react";
import type {
  EstadisticasConversaciones, EstadisticasMarca, EstadisticasPlantillas, EstadisticasTwilio, OrdenPlantillas, RangoFechas, ResultadoSincronizacion,
} from "../types.estadisticas";
import { estadisticasApi } from "../estadisticasApi";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import type { AvisoSincronizacion, ClaveRango } from "../logic/estadisticas";
import { BASE_TASA, LEIDO_ACLARACION, NOTA_BASES_TASAS, TEXTO_SIN_ENVIOS, avisoSincronizacion, sobreLaBase, nombreCsv, rangoDesdeParams, serieACsv, tasaTexto, textoSync } from "../logic/estadisticas";
import { usd } from "../logic/formato";
import { Tarjeta } from "../components/Tarjeta";
import StatTile from "../../campana-nueva/components/StatTile";
import { num } from "../../campana-nueva/utils/format";
import { ChipSimulado, ContenidoTarjeta, ErrorTarjeta, Esqueleto, Vacio } from "../components/estadisticas/comun";
import type { EstadoPedido } from "../components/estadisticas/comun";
import { SelectorRango } from "../components/estadisticas/SelectorRango";
import { GraficoSerieDiaria } from "../components/estadisticas/GraficoSerieDiaria";
import { TarjetaTiempos } from "../components/estadisticas/TarjetaTiempos";
import { TarjetaErrores } from "../components/estadisticas/TarjetaErrores";
import { TarjetaRespuestas } from "../components/estadisticas/TarjetaRespuestas";
import { TarjetaCostos } from "../components/estadisticas/TarjetaCostos";
import { TarjetaCostoPorEnvio } from "../components/estadisticas/TarjetaCostoPorEnvio";
import { TarjetaCuentaTwilio } from "../components/estadisticas/TarjetaCuentaTwilio";
import { TarjetaNumero } from "../components/estadisticas/TarjetaNumero";
import { TablaPlantillas } from "../components/estadisticas/TablaPlantillas";
import { TarjetaClics } from "../components/estadisticas/TarjetaClics";
import { TarjetaErroresTwilio } from "../components/estadisticas/TarjetaErroresTwilio";
import { TarjetaSoloConsola } from "../components/estadisticas/TarjetaSoloConsola";
import { SeccionConversaciones } from "../components/estadisticas/SeccionConversaciones";
import { DetalleTecnico } from "../components/estadisticas/DetalleTecnico";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { Columns3 } from "lucide-react";

type Pedido<D> = { dato: D | null; estado: EstadoPedido; recargar: () => void };

const CLS_AVISO_SYNC: Record<AvisoSincronizacion["tono"], string> = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
  neutro: "border-slate-200 bg-slate-50 text-slate-700",
  error: "border-amber-200 bg-amber-50 text-amber-800",
};

/**
 * Un pedido con su propio estado. `clave` identifica el rango: al cambiar, lo anterior se borra
 * (no se muestran datos de otro rango bajo el título del nuevo) y una respuesta vieja que llega
 * tarde se descarta por el número de secuencia.
 */
/** `activo` en false no pide nada (lo de Twilio para una persona de un cliente: el backend se lo niega). */
function usePedido<D>(pedir: () => Promise<unknown>, clave: string, activo = true): Pedido<D> {
  const [dato, setDato] = useState<D | null>(null);
  const [estado, setEstado] = useState<EstadoPedido>({ cargando: true, error: "" });
  const seq = useRef(0);
  const claveVista = useRef(clave);
  const pedirRef = useRef(pedir);
  pedirRef.current = pedir;

  const correr = useCallback(async () => {
    const mio = ++seq.current;
    setEstado((e) => ({ ...e, cargando: true }));
    let error = "";
    let nuevo: D | null = null;
    try {
      const l = leerRespuesta<D>(await pedirRef.current());
      if (!l.ok) error = textoError(l);
      else if (l.data === undefined || l.data === null) error = "El servidor no devolvió datos.";
      else nuevo = l.data;
    } catch (e) {
      error = textoError(leerError(e));
    }
    if (mio !== seq.current) return;
    if (nuevo !== null) setDato(nuevo);
    setEstado({ cargando: false, error });
  }, []);

  useEffect(() => {
    if (!activo) return;
    if (claveVista.current !== clave) { claveVista.current = clave; setDato(null); }
    void correr();
  }, [clave, correr, activo]);

  return { dato, estado, recargar: () => void correr() };
}

export default function EstadisticasOficialPage() {
  const [sp, setSp] = useSearchParams();
  const spTexto = sp.toString();
  // «Hoy» se fija al cambiar la URL: un re-render a medianoche no mueve el rango solo.
  const { rango, clave, error: errorUrl } = useMemo(() => rangoDesdeParams(new URLSearchParams(spTexto), new Date()), [spTexto]);
  const claveRango = `${rango.desde}|${rango.hasta}`;
  const [orden, setOrden] = useState<OrdenPlantillas>("enviados");

  const marca = usePedido<EstadisticasMarca>(() => estadisticasApi.marca(rango), claveRango);
  const plantillas = usePedido<EstadisticasPlantillas>(() => estadisticasApi.plantillas(rango), claveRango);
  const conversaciones = usePedido<EstadisticasConversaciones>(() => estadisticasApi.conversaciones(rango), claveRango);
  /**
   * Lo de Twilio (costos según Twilio, la cuenta, el número, sus errores y «Actualizar datos», que
   * los trae) es de la cuenta de Deenex: una persona de un cliente (Accesos, rol «comercio») no lo
   * pide ni lo ve. Ve sus envíos, las respuestas, las conversaciones y las plantillas.
   */
  const equipo = useEsEquipoDeenex();
  const twilio = usePedido<EstadisticasTwilio>(() => estadisticasApi.twilio(rango), claveRango, equipo);

  const [sincronizando, setSincronizando] = useState(false);
  const [avisoSync, setAvisoSync] = useState<AvisoSincronizacion | null>(null);

  const elegir = (c: ClaveRango) => setSp(new URLSearchParams({ rango: c }), { replace: true });
  const personalizado = (r: RangoFechas) => setSp(new URLSearchParams({ desde: r.desde, hasta: r.hasta }), { replace: true });

  const sincronizar = async () => {
    if (sincronizando) return;
    setSincronizando(true);
    setAvisoSync(null);
    try {
      const l = leerRespuesta<ResultadoSincronizacion>(await estadisticasApi.sincronizar());
      if (!l.ok) { setAvisoSync({ tono: "error", texto: textoError(l) }); return; }
      // Sin número, lo del número vuelve {ok:false} con «falta el número»: es un aviso, no una falla.
      setAvisoSync(avisoSincronizacion(l.data));
      twilio.recargar();
      marca.recargar();
      plantillas.recargar();
      conversaciones.recargar();
    } catch (e) {
      const x = leerError(e);
      setAvisoSync({ tono: "error", texto: x.status === 429 ? "Ya se actualizó hace menos de un minuto" : textoError(x) });
    } finally {
      setSincronizando(false);
    }
  };

  const descargarCsv = () => {
    const serie = marca.dato?.serie;
    if (!serie) return;
    try {
      // Sin lo que le cuesta a Deenex ni lo que informa Twilio para una persona de un cliente (`serieACsv`).
      const url = URL.createObjectURL(new Blob([serieACsv(serie, { equipo })], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = nombreCsv(rango);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL?.(url), 1000);
    } catch {
      setAvisoSync({ tono: "error", texto: "No pudimos armar el archivo. Probá de nuevo." });
    }
  };

  const hayDatosSimulados = !!(marca.dato?.hayDatosSimulados || plantillas.dato?.hayDatosSimulados || conversaciones.dato?.hayDatosSimulados || twilio.dato?.simulado);
  const m = marca.dato;
  const t = twilio.dato;
  // Sin envíos en el período, la evolución, las respuestas y las plantillas dirían lo mismo tres veces («no hay
  // envíos»): lo dice una vez la fila de números y esas tarjetas no se pintan (auditoría de diseño del 18/10/2026).
  const sinEnviosEnElPeriodo = !!m && !(Number(m.totales?.enviados) > 0) && !(Number(m.totales?.campanas) > 0);

  return (
    <div className="space-y-6">
      {/* 1. Encabezado */}
      <header className="space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">Estadísticas</h2>
              {hayDatosSimulados && <ChipSimulado />}
            </div>
            <p className="text-sm text-slate-500 mt-1">Lo que pasó con tus envíos y cómo se contestó a quienes escribieron</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* «Comparar campañas» con las mismas fechas (el rango viaja en la URL) */}
            <Link to={`/activa/oficial/campanas/comparar${spTexto ? `?${spTexto}` : ""}`}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Columns3 size={15} /> Comparar campañas
            </Link>
            <button type="button" onClick={descargarCsv} disabled={!m?.serie}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              <Download size={15} /> Descargar CSV
            </button>
            {/* Trae lo de Twilio (POST /estadisticas/twilio/sincronizar): de la cuenta de Deenex, sólo el equipo. */}
            {equipo && (
              <button type="button" onClick={() => void sincronizar()} disabled={sincronizando}
                className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                {sincronizando ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Actualizar datos
              </button>
            )}
          </div>
        </div>
        <SelectorRango clave={clave} rango={rango} onElegir={elegir} onPersonalizado={personalizado} />
        {errorUrl && <p className="text-xs text-amber-700" role="status">{errorUrl}</p>}
        {avisoSync && (
          <p role={avisoSync.tono === "error" ? "alert" : "status"}
            className={`rounded-xl border px-4 py-2.5 text-sm ${CLS_AVISO_SYNC[avisoSync.tono]}`}>
            {avisoSync.texto}
          </p>
        )}
      </header>

      {/* ── Lo del negocio ── */}
      <Kpis marca={marca} />

      {!sinEnviosEnElPeriodo && (<>
      <Tarjeta titulo="Evolución por día">
        <ContenidoTarjeta dato={m} estado={marca.estado} onReintentar={marca.recargar} alto="h-56" queFalta="la evolución">
          {(d) => <GraficoSerieDiaria serie={d.serie} />}
        </ContenidoTarjeta>
      </Tarjeta>

      <Tarjeta titulo="Respuestas">
        <ContenidoTarjeta dato={m} estado={marca.estado} onReintentar={marca.recargar} queFalta="las respuestas">
          {(d) => (
            <TarjetaRespuestas botones={d.botones} bajasPorVia={d.bajasPorVia} optOut={d.optOut}
              entrantesPorAnuncio={d.totales?.entrantesPorAnuncio} ventanasAbiertas24h={d.totales?.ventanasAbiertas24h}
              respuestasCitando={d.totales?.respuestasCitando} />
          )}
        </ContenidoTarjeta>
      </Tarjeta>
      </>)}

      {/* Conversaciones: su propio pedido, con su carga y su error (puede haber consultas sin envíos) */}
      <SeccionConversaciones dato={conversaciones.dato} estado={conversaciones.estado} onReintentar={conversaciones.recargar} rango={rango} />

      {!sinEnviosEnElPeriodo && (
      <Tarjeta titulo="Plantillas" sub="Rendimiento en estas fechas">
        <ContenidoTarjeta dato={plantillas.dato} estado={plantillas.estado} onReintentar={plantillas.recargar} queFalta="las plantillas">
          {(d) => (
            <div className="space-y-3">
              <ResumenPlantillas d={d} />
              <TablaPlantillas filas={d.filas} orden={orden} onOrden={setOrden} />
            </div>
          )}
        </ContenidoTarjeta>
      </Tarjeta>
      )}

      {/* Clics (sólo con Link Shortening) */}
      {m?.clics?.activo && (
        <Tarjeta titulo="Clics en los links">
          <TarjetaClics clics={m.clics} />
        </Tarjeta>
      )}

      {/* ── Lo técnico: plegado. Los pedidos corren igual, así abrirlo no espera a nadie. ── */}
      {/* Lo de Twilio (la sincronización, lo que factura, la cuenta, el número y sus errores) es de la cuenta de
          Deenex: una persona de un cliente ve acá sólo los tiempos y los errores de sus envíos. */}
      <DetalleTecnico conFalla={!!t?.sync?.errores?.length}
        subtitulo={equipo ? "Tiempos de entrega, códigos de error, lo que factura Twilio, la cuenta y el número" : "Tiempos de entrega y por qué no llegaron algunos mensajes"}>
        {t && <p className="text-xs text-slate-500">{textoSync(t.sync, new Date())}</p>}
        {!!t?.sync?.errores?.length && (
          <p className="flex items-start gap-2 text-xs text-amber-700">
            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
            Última falla al traer datos de Twilio ({t.sync.errores[t.sync.errores.length - 1].tarea}): {t.sync.errores[t.sync.errores.length - 1].mensaje}
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <Tarjeta titulo="Tiempos de entrega" sub="Mediana y en cuánto llegan 9 de cada 10">
            <ContenidoTarjeta dato={m} estado={marca.estado} onReintentar={marca.recargar} queFalta="los tiempos">
              {(d) => <TarjetaTiempos tiempos={d.tiempos} />}
            </ContenidoTarjeta>
          </Tarjeta>

          {/* «Twilio» es del equipo: a una persona de un cliente, por qué no llegaron, sin nombrar al proveedor. */}
          <Tarjeta titulo="Errores de los envíos" sub={equipo ? "Por código de Twilio o Meta" : "Por qué no llegaron algunos mensajes"}>
            <ContenidoTarjeta dato={m} estado={marca.estado} onReintentar={marca.recargar} queFalta="los errores">
              {(d) => <TarjetaErrores errores={d.errores} />}
            </ContenidoTarjeta>
          </Tarjeta>
        </div>

        {equipo && (<>
        <Tarjeta titulo="Costo real por envío" sub="Lo que facturó Twilio en estas fechas, dividido por los mensajes que salieron">
          <ContenidoTarjeta dato={t} estado={twilio.estado} onReintentar={twilio.recargar} alto="h-32" queFalta="los datos de Twilio">
            {(d) => <TarjetaCostoPorEnvio costo={d.costoPorEnvio} twilio={d} />}
          </ContenidoTarjeta>
        </Tarjeta>

        <Tarjeta titulo="Costos según Twilio" sub="Lo que Twilio facturó por día, contra lo que estimaba nuestra tabla">
          <ContenidoTarjeta dato={t} estado={twilio.estado} onReintentar={twilio.recargar} alto="h-56" queFalta="los datos de Twilio">
            {(d) => <TarjetaCostos twilio={d} serie={m?.serie ?? null} costoMarca={m?.costo ?? null} />}
          </ContenidoTarjeta>
        </Tarjeta>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <Tarjeta titulo="Cuenta de Twilio">
            <ContenidoTarjeta dato={t} estado={twilio.estado} onReintentar={twilio.recargar} queFalta="los datos de Twilio">
              {(d) => <TarjetaCuentaTwilio twilio={d} />}
            </ContenidoTarjeta>
          </Tarjeta>

          <Tarjeta titulo="Número de WhatsApp" sub="Calidad y límite que pone Meta">
            <ContenidoTarjeta dato={t} estado={twilio.estado} onReintentar={twilio.recargar} queFalta="los datos del número">
              {(d) => <TarjetaNumero twilio={d} />}
            </ContenidoTarjeta>
          </Tarjeta>
        </div>

        <Tarjeta titulo="Errores que registró Twilio" sub="Últimos 30 días: Twilio no guarda más">
          <ContenidoTarjeta dato={t} estado={twilio.estado} onReintentar={twilio.recargar} queFalta="los errores de Twilio">
            {(d) => <TarjetaErroresTwilio twilio={d} />}
          </ContenidoTarjeta>
        </Tarjeta>

        <Tarjeta titulo="Sólo en la consola de Twilio" sub="Twilio no da estos datos por API">
          <TarjetaSoloConsola items={t?.soloConsola} />
        </Tarjeta>
        </>)}
      </DetalleTecnico>
    </div>
  );
}

function Kpis({ marca }: { marca: Pedido<EstadisticasMarca> }) {
  const m = marca.dato;
  if (!m) {
    if (marca.estado.error) return <ErrorTarjeta texto={`No pudimos traer los números del período. ${marca.estado.error}`} onReintentar={marca.recargar} />;
    return (
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" aria-busy="true" aria-label="Cargando los números del período">
        {[0, 1, 2, 3, 4, 5].map((i) => <Esqueleto key={i} alto="h-32" />)}
      </div>
    );
  }
  const sinEnvios = !(Number(m.totales?.enviados) > 0) && !(Number(m.totales?.campanas) > 0);
  const tasa = (v: number | undefined): string | null => (sinEnvios ? null : tasaTexto(v));
  const tiles: ReactNode = (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
      <StatTile icon={<Send size={15} />} label="Enviados" value={sinEnvios ? null : num(m.totales.enviados)} vacio="Sin envíos"
        sub={`${num(m.totales.campanas)} ${m.totales.campanas === 1 ? "campaña" : "campañas"}`} tono="entrega" />
      {/* Cada tasa dice su base: entrega y bajas van sobre los enviados, lectura y respuesta sobre los entregados. */}
      <StatTile icon={<CheckCheck size={15} />} label="Entrega" value={tasa(m.tasas?.entrega)} vacio="Sin envíos" tono="entrega"
        meterPct={sinEnvios ? undefined : (m.tasas?.entrega || 0) * 100} sub={sobreLaBase(m.totales.entregados, m.totales.enviados, BASE_TASA.entrega)} />
      <StatTile icon={<Eye size={15} />} label="Lectura" value={tasa(m.tasas?.lectura)} vacio="Sin envíos" tono="lectura"
        sub={sobreLaBase(m.totales.leidos, m.totales.entregados, BASE_TASA.lectura)} />
      <StatTile icon={<MessageSquare size={15} />} label="Respuesta" value={tasa(m.tasas?.respuesta)} vacio="Sin envíos" tono="respuesta"
        sub={sobreLaBase(m.totales.respuestas, m.totales.entregados, BASE_TASA.respuesta)} />
      <StatTile icon={<UserMinus size={15} />} label="Bajas" value={tasa(m.tasas?.bajas)} vacio="Sin envíos" tono="baja" sub={sobreLaBase(m.totales.bajas, m.totales.enviados, BASE_TASA.bajas)} />
      <StatTile icon={<Wallet size={15} />} label="Gasto del período" value={usd(m.costo?.cobradoClienteUSD)} tono="resultado" sub="Cobrado a la marca"
        chip={m.hayDatosSimulados ? { texto: "simulado", cls: "bg-amber-50 text-amber-700" } : undefined} />
    </div>
  );
  return (
    <div className="space-y-3">
      {marca.estado.error && <p className="text-xs text-amber-700">No pudimos actualizar: {marca.estado.error} <button type="button" onClick={marca.recargar} className="font-semibold underline">Reintentar</button></p>}
      {sinEnvios && <Vacio>{TEXTO_SIN_ENVIOS}.</Vacio>}
      {tiles}
      {!sinEnvios && <p className="text-[11px] text-slate-400">{NOTA_BASES_TASAS} {LEIDO_ACLARACION}</p>}
    </div>
  );
}

function ResumenPlantillas({ d }: { d: EstadisticasPlantillas }) {
  const r = d.resumen;
  if (!r) return null;
  const partes: string[] = [];
  if (r.medianaHorasAprobacion !== null && Number.isFinite(Number(r.medianaHorasAprobacion))) {
    partes.push(`Meta tarda ${num(Math.round(Number(r.medianaHorasAprobacion) * 10) / 10)} h en aprobar (mediana)`);
  }
  if (r.recategorizadas > 0) partes.push(`${num(r.recategorizadas)} ${r.recategorizadas === 1 ? "recategorizada" : "recategorizadas"} por Meta`);
  if (!partes.length) return null;
  return <p className="text-xs text-slate-500">{partes.join(" · ")}</p>;
}
